/*
  The wrapper combines the things looking at or waiting for logs with the actual server logs
*/

import Logger from '@/logger';
import soft from '@/softconfig';
import BrickadiaServer from '@brickadia/server';
import { type IConfig } from '@config/types';
import EventEmitter from 'events';
import path from 'path';
import { migrateConsoleCommand } from './commands';
import LogWrangler from './logWrangler';
import type Omegga from './server';

class OmeggaWrapper extends EventEmitter {
  #server: BrickadiaServer;
  dataPath: string;
  path: string;

  logWrangler: LogWrangler;
  addMatcher: LogWrangler['addMatcher'];
  addWatcher: LogWrangler['addWatcher'];
  watchLogArray: LogWrangler['watchLogArray'];
  watchLogChunk: LogWrangler['watchLogChunk'];

  config: IConfig;

  constructor(serverPath: string, cfg: IConfig) {
    super();
    this.setMaxListeners(Infinity);

    this.config = cfg;
    this.path =
      path.isAbsolute(serverPath) || serverPath.startsWith('/')
        ? serverPath
        : path.join(process.cwd(), serverPath);
    this.dataPath = path.join(this.path, soft.DATA_PATH);
    this.#server = new BrickadiaServer(this.dataPath, cfg);

    // log wrangler wrangles logs... it reads brickadia logs and clumps them together
    // this is cursed but the OmeggaWrapper will never be used without omegga...
    this.logWrangler = new LogWrangler(this as unknown as Omegga);
    this.#server.on('line', this.logWrangler.callback);
    this.#server.on('line', (line: string) => this.emit('line', line));
    this.#server.on('err', (line: string) => this.emit('err', line));
    this.#server.on('closed', () => this.emit('closed'));

    this.addMatcher = this.logWrangler.addMatcher;
    this.addWatcher = this.logWrangler.addWatcher;
    this.watchLogArray = this.logWrangler.watchLogArray;
    this.watchLogChunk = this.logWrangler.watchLogChunk;
  }

  // passthrough to server
  write(str: string) {
    this.#server.write(str);
  }
  writeln(str: string) {
    // auto-migrate stale console command names to the running game version's
    // name (e.g. `Bricks.Clear` -> `br.Bricks.Clear`) so older plugins keep
    // working. OmeggaWrapper is never used without Omegga, so `version` exists.
    this.#server.writeln(
      migrateConsoleCommand(str, (this as unknown as Omegga).version),
    );
  }
  start() {
    return this.#server.start();
  }
  stop() {
    return this.#server.stop();
  }

  // event emitter to catch everything
  emit(type: string, ...args: unknown[]) {
    if (type !== 'line') Logger.verbose('Emitting event', type);
    try {
      (super.emit as EventEmitter['emit'])('*', type, ...args);
    } catch (e) {
      Logger.errorp('Error in emitted event', type, e);
      // error emitting
    }
    return (super.emit as EventEmitter['emit'])(type, ...args);
  }

  /** Get which world will be loaded on startup (form file, config, or env) */
  getNextWorld() {
    return this.#server.getNextWorld();
  }

  /** Get the configured default world */
  getActiveWorld() {
    return this.#server.getActiveWorld();
  }

  /** Configure the default world  */
  setActiveWorld(world: string | null): boolean {
    return this.#server.setActiveWorld(world);
  }

  /** Check if a world exists */
  worldExists(file: string) {
    return this.#server.worldExists(file);
  }

  /** Resolve the game server binary path (steam/override installs; null for launcher) */
  getGameBinaryPath(): string | null {
    return this.#server.getGameBinaryPath();
  }

  /** Resolve the directory containing the game server binary */
  getGameBinaryDir(): string | null {
    return this.#server.getGameBinaryDir();
  }

  /** pid of the running game process, or null when it isn't running */
  get gamePid(): number | null {
    return this.#server.pid;
  }
}

export default OmeggaWrapper;
